import React, { useEffect, useMemo, useRef, useState } from 'react';

import dayjs from '~/timeInit';

import { Box } from '@mui/material';

import { useGetChainLpcLppRollupTrend } from '~/hooks/apis/price-parity/useGetChainLpcLppRollupTrend';
import { useGetChainLpcLppSupplierBreakdowns } from '~/hooks/apis/price-parity/useGetChainLpcLppSupplierBreakdowns';
import { useGetChainLpcLppSupplierChains } from '~/hooks/apis/price-parity/useGetChainLpcLppSupplierChains';

import type { ChainLpcLppSupplierPropertyGroup } from '~/types/services/priceParrot';

import DateRangeSlider from '../PriceParrotParityTool/DateRangeSlider';
import {
  buildCalendarDatesInclusive,
  getParityTrendFullWindowStart,
} from '../PriceParrotParityTool/parityTrendDateRange';

import ChainAndRegionPicker from './ChainAndRegionPicker';
import FilterControls from './FilterControls';
import HotelRatioTrendModal from './HotelRatioTrendModal';
import PropertiesList from './PropertiesList';
import RollupSummaryTiles from './RollupSummaryTiles';
import RollupTrendChart from './RollupTrendChart';
import { DisplaySortMode, HOTSPOT_RATIO_THRESHOLD, PROPERTIES_PAGE_SIZE } from './constants';
import { lpcToLppRatio } from './ratio';
import { computeChainRollupOverview, emptyStateMessage, hasLppSupplier, presetKeyFor } from './utils';

export default function PriceParrotChainLpcLppRatioDashboard() {
  const { calendarDates, initialRange } = useMemo(() => {
    const end = dayjs().startOf('day');
    const start = getParityTrendFullWindowStart(end);
    const dates = buildCalendarDatesInclusive(start, end);
    const len = dates.length;
    const range: [number, number] = len > 1 ? [Math.max(0, len - 365), len - 1] : [0, 0];
    return { calendarDates: dates, initialRange: range };
  }, []);

  const [committedIndices, setCommittedIndices] = useState<[number, number]>(initialRange);
  const dateFrom = calendarDates[committedIndices[0]] ?? '';
  const dateTo = calendarDates[committedIndices[1]] ?? '';

  const [sort, setSort] = useState<DisplaySortMode>('views');
  const [mixedSppOnly, setMixedSppOnly] = useState(true);
  const [hotspotsOnly, setHotspotsOnly] = useState(true);
  const [region, setRegion] = useState<string | null>(null);
  const [trendGroup, setTrendGroup] = useState<ChainLpcLppSupplierPropertyGroup | null>(null);
  const [page, setPage] = useState(1);
  const [selectedChains, setSelectedChains] = useState<Array<string>>([]);

  const {
    data: chainsListData,
    isLoading: isChainsLoading,
    isError: isChainsError,
    error: chainsError,
  } = useGetChainLpcLppSupplierChains(dateFrom, dateTo);

  // The unfiltered chain list — feeds auto-pick/pruning and the master region list.
  const allChains = useMemo(
    () => [...(chainsListData?.chains ?? [])].sort((a, b) => a.chain_name.localeCompare(b.chain_name)),
    [chainsListData?.chains],
  );
  const allChainNames = useMemo(() => allChains.map((c) => c.chain_name), [allChains]);

  // Region options span every chain, not just the currently selected ones.
  const regionOptions = useMemo<Array<string>>(() => {
    const regions = new Set<string>();
    for (const chain of allChains) {
      for (const chainRegion of chain.regions) {
        regions.add(chainRegion);
      }
    }
    return Array.from(regions).sort((a, b) => a.localeCompare(b));
  }, [allChains]);

  const chainOptions = useMemo(() => {
    if (!region) {
      return allChainNames;
    }
    return allChains.filter((c) => c.regions.includes(region)).map((c) => c.chain_name);
  }, [allChains, allChainNames, region]);

  // Auto-pick the first chain alphabetically, once — a bare length-check would refire and
  // reinstate a default every time the user deliberately clears their selection.
  const hasAutoPickedChain = useRef(false);
  useEffect(() => {
    if (!allChainNames.length || hasAutoPickedChain.current) {
      return;
    }
    hasAutoPickedChain.current = true;
    setSelectedChains([allChainNames[0]]);
  }, [allChainNames]);

  useEffect(() => {
    if (!allChainNames.length || selectedChains.length === 0) {
      return;
    }
    const stillValid = selectedChains.filter((c) => allChainNames.includes(c));
    if (stillValid.length !== selectedChains.length) {
      setSelectedChains(stillValid);
    }
  }, [allChainNames, selectedChains]);

  useEffect(() => {
    if (region && !regionOptions.includes(region)) {
      setRegion(null);
    }
  }, [region, regionOptions]);

  // Picking a region drops any already-selected chain that doesn't have it.
  const handleRegionChange = (nextRegion: string | null) => {
    setRegion(nextRegion);
    if (nextRegion) {
      const chainsInRegion = new Set(allChains.filter((c) => c.regions.includes(nextRegion)).map((c) => c.chain_name));
      setSelectedChains((prev) => prev.filter((chainName) => chainsInRegion.has(chainName)));
    }
  };

  // 'ratio_asc' has no server-side equivalent, so request the default order
  // and re-sort client-side below instead.
  const {
    properties: fetchedProperties,
    isFetching: isBreakdownFetching,
    isError: isBreakdownError,
    error: breakdownError,
  } = useGetChainLpcLppSupplierBreakdowns(selectedChains, dateFrom, dateTo, sort === 'ratio_asc' ? 'views' : sort);

  const {
    daily: rollupTrendDaily,
    isFetching: isRollupTrendFetching,
    isError: isRollupTrendError,
    error: rollupTrendError,
  } = useGetChainLpcLppRollupTrend(selectedChains, dateFrom, dateTo, selectedChains.length > 0);

  const activePresetKey = useMemo(
    () => presetKeyFor(committedIndices, calendarDates.length),
    [committedIndices, calendarDates.length],
  );

  const handlePeriodPreset = (days: number) => {
    const maxIdx = calendarDates.length - 1;
    if (maxIdx < 0) {
      return;
    }
    const fromIdx = Math.max(0, maxIdx - (days - 1));
    setCommittedIndices([fromIdx, maxIdx]);
  };

  // No bedbank competition means no LPC:LPP ratio to show.
  const lppMixedProperties = useMemo(() => fetchedProperties.filter(hasLppSupplier), [fetchedProperties]);

  const displayedProperties = useMemo(() => {
    let properties = mixedSppOnly ? lppMixedProperties : fetchedProperties;
    if (region) {
      properties = properties.filter((g) => g.region === region);
    }
    if (hotspotsOnly) {
      properties = properties.filter((g) => {
        const ratio = lpcToLppRatio(g);
        return ratio != null && ratio < HOTSPOT_RATIO_THRESHOLD;
      });
    }
    if (sort === 'ratio_asc') {
      // Properties with no LPP bookings have no ratio to rank by — push them last.
      properties = [...properties].sort((a, b) => {
        const ratioA = lpcToLppRatio(a) ?? Infinity;
        const ratioB = lpcToLppRatio(b) ?? Infinity;
        return ratioA - ratioB;
      });
    }
    return properties;
  }, [mixedSppOnly, lppMixedProperties, fetchedProperties, region, hotspotsOnly, sort]);

  // Always recomputed client-side — a merged multi-chain fetch has no single
  // server-side rollup to fall back on once >1 chain is selected.
  const overview = useMemo(() => computeChainRollupOverview(displayedProperties), [displayedProperties]);

  const pageCount = Math.max(1, Math.ceil(displayedProperties.length / PROPERTIES_PAGE_SIZE));
  const visibleProperties = displayedProperties.slice((page - 1) * PROPERTIES_PAGE_SIZE, page * PROPERTIES_PAGE_SIZE);

  useEffect(() => {
    setPage(1);
  }, [selectedChains, dateFrom, dateTo, sort, mixedSppOnly, hotspotsOnly, region]);

  return (
    <Box>
      <ChainAndRegionPicker
        chainOptions={chainOptions}
        selectedChains={selectedChains}
        onChainsChange={setSelectedChains}
        isChainsLoading={isChainsLoading}
        isChainsError={isChainsError}
        chainsError={chainsError}
        regionOptions={regionOptions}
        region={region}
        onRegionChange={handleRegionChange}
      />

      <Box>
        <FilterControls
          activePresetKey={activePresetKey}
          onPeriodPresetChange={handlePeriodPreset}
          sort={sort}
          onSortChange={setSort}
          mixedSppOnly={mixedSppOnly}
          onMixedSppOnlyChange={setMixedSppOnly}
          hotspotsOnly={hotspotsOnly}
          onHotspotsOnlyChange={setHotspotsOnly}
        />

        <Box sx={{ mb: 2 }}>
          <DateRangeSlider
            dates={calendarDates}
            range={committedIndices}
            onRangeChange={(next) => setCommittedIndices(next as [number, number])}
          />
        </Box>

        <RollupSummaryTiles overview={overview} />

        <RollupTrendChart
          daily={rollupTrendDaily}
          isFetching={isRollupTrendFetching}
          isError={isRollupTrendError}
          error={rollupTrendError}
        />

        <PropertiesList
          isFetching={isBreakdownFetching}
          isError={isBreakdownError}
          error={breakdownError}
          displayedProperties={displayedProperties}
          visibleProperties={visibleProperties}
          emptyMessage={emptyStateMessage(mixedSppOnly, hotspotsOnly, region)}
          page={page}
          pageCount={pageCount}
          onPageChange={setPage}
          onShowTrend={setTrendGroup}
        />
      </Box>

      <HotelRatioTrendModal
        group={trendGroup}
        dateFrom={dateFrom}
        dateTo={dateTo}
        onClose={() => setTrendGroup(null)}
      />
    </Box>
  );
}
